import { createContext, useState, useMemo, useCallback } from 'react'
import {
  ROW_HEADER_HEIGHT,
  ROW_HEIGHTS_BY_MODE,
  ROW_MIN_HEIGHT_COMPACT,
} from '../ReadGridConstants'
import { RowPropertyName } from '../readGridToolbar/components/toggleColumns/types'

export const COL_WIDTHS = {
  cvId: 265,
  sourceUrl: 300,
  default: 200,
  fixed: 50,
  min: 80,
}

export const ReadGridColumnWidthContext = createContext({ rowMode: '' })

ReadGridColumnWidthContext.displayName = 'ReadGridColumnWidthContext'

export const ReadGridColumnWidthProvider = ({ columns, multi, children, rowMode }) => {
  const [columnWidths, setColumnWidths] = useState({})
  const [rowHeights] = useState({})

  const getColumnWidth = useCallback(
    index => {
      // if it's been overwritten, use that
      // if multiSelect is rendered, then decrement index, bc columnWidths is based on original indexing
      if (!multi && columnWidths[index]) {
        return columnWidths[index]
      }
      if (multi && columnWidths[index - 1]) {
        return columnWidths[index - 1]
      }

      const column = columns[index]
      const isCvIdColumn =
        column?.governanceType === 'cv_id' || column?.rowPropertyName === RowPropertyName.cvId

      if (isCvIdColumn) {
        return COL_WIDTHS.cvId
      }

      if (column?.isSourceUrl) return COL_WIDTHS.sourceUrl

      const isFixedWidthColumn = index === 0 || (multi && index === 1)

      if (isFixedWidthColumn) {
        return COL_WIDTHS.fixed
      }

      return COL_WIDTHS.default
    },
    [columns, multi, columnWidths],
  )

  const getRowHeight = useCallback(
    ({ index }) => {
      // if it's been overwritten, use that
      if (rowHeights[index]) {
        return rowHeights[index]
      }
      const rowHeight = ROW_HEIGHTS_BY_MODE[rowMode] || ROW_MIN_HEIGHT_COMPACT
      return index === 0 ? ROW_HEADER_HEIGHT : rowHeight
    },
    [rowHeights, rowMode],
  )

  const updateColumnWidth = useCallback(
    (columnIndex, delta) => {
      const currentWidth = getColumnWidth(columnIndex)

      // if multiSelect is rendered, use decremented index, bc columnWidths is based on original indexing
      const multiColumnIndex = multi ? columnIndex - 1 : columnIndex

      setColumnWidths({
        ...columnWidths,
        [multiColumnIndex]:
          columnIndex == 0 ? currentWidth + delta : Math.max(COL_WIDTHS.min, currentWidth + delta),
      })
    },
    [columnWidths],
  )

  const augmentedValue = useMemo(
    () => ({
      columnWidths,
      rowMode,
      maxColumnWidth: Math.max(...Object.values(COL_WIDTHS)),
      updateColumnWidth,
      getColumnWidth,
      getRowHeight,
    }),
    [columnWidths, updateColumnWidth, getColumnWidth, getRowHeight, rowMode, multi],
  )

  return (
    <ReadGridColumnWidthContext.Provider value={augmentedValue}>
      <ReadGridColumnWidthContext.Consumer>
        {widthContext => {
          return children(widthContext)
        }}
      </ReadGridColumnWidthContext.Consumer>
    </ReadGridColumnWidthContext.Provider>
  )
}

ReadGridColumnWidthProvider.propTypes = {}
